import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { AppComponent } from '../../app.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { AccommodationService } from '../../features/accommodation/services/accommodation.service';

@Component({
  selector: 'app-accommodation-log',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './accommodation-log.component.html',
  styleUrl: './accommodation-log.component.scss',
})
export class AccommodationLogComponent implements OnInit {
  public viewPortSize: boolean = false;
  public rowData: any[] | null = null;
  public loading: boolean = true;
  public error: any = null;
  private searchSubject = new Subject<string>();
  readonly dialog = inject(MatDialog);
  startDate: any;
  endDate: any;
  dateRangeForm: FormGroup;
  accommodationId: any;

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time component
    return date ? date <= today : false;
  };

  columnDefs: (
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter?: undefined;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        headerName: string;
        field: string;
        flex: number;
        valueFormatter: (params: any) => string;
        cellRenderer?: undefined;
        floatingFilter?: undefined;
        filter?: undefined;
      }
    | {
        field: string;
        headerName: string;
        flex: number;
        cellRenderer: (params: any) => HTMLDivElement;
        floatingFilter: boolean;
        filter: boolean;
        valueFormatter?: undefined;
      }
  )[];
  page = 0;
  pageSize = 10;
  totalPages = 1;
  pageSizeOptions = [5, 10, 20, 50, 100];
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Employee Name',
        field: 'employeeName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Start Date',
        field: 'startDate',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'End Date',
        field: 'endDate',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Days Occupied',
        field: 'daysOccupied',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];
  }

  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public themeClass: string = 'ag-theme-quartz';
  today = new Date();
  firstDayOfMonth = new Date(
    this.today.getFullYear(),
    this.today.getMonth(),
    1
  );
  lastDayOfMonth = new Date(
    this.today.getFullYear(),
    this.today.getMonth() + 1,
    0
  );

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private accommodationService: AccommodationService,
    private datePipe: DatePipe,
    private appComponent: AppComponent,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.dateRangeForm = this.fb.group({
      startDate: [this.firstDayOfMonth],
      endDate: [this.lastDayOfMonth],
    });
    this.startDate = this.firstDayOfMonth;
    this.endDate = this.lastDayOfMonth;

    this.route.queryParamMap.subscribe((queryParams) => {
      this.accommodationId = queryParams.get('accommodationId');
      this.getEmployeesInProperties();
    });
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getEmployeesInProperties(searchTerm);
      });

    this.checkViewportSize();

    this.appComponent.toggleSidebar();
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(searchTerm);
  }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.getEmployeesInProperties('', this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.getEmployeesInProperties('', this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    console.log('New Page Size:', newSize);
    this.pageSize = newSize;
    this.page = 0;
    this.getEmployeesInProperties('', this.pageSize);
  }

  onDateChange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.getEmployeesInProperties();
    }
  }
  getEmployeesInProperties(search: string = '', pageSize: number = 10) {
    this.loading = true;
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;

    const input = {
      propertyId: Number(this.accommodationId),
      limit: this.pageSize,
      skip: skip,
      search: search,
      startDate: this.startDate,
      endDate: this.endDate,
    };
    this.accommodationService.getEmployeesInProperties(input).subscribe({
      next: (data) => {
        const employees =
          data?.propertyAllocations?.length > 0
            ? data.propertyAllocations[0]?.employees || []
            : [];

        this.totalPages = Math.ceil(
          (data?.total || employees.length) / this.pageSize
        );

        this.rowData = employees.map((employee: any) => {
          return {
            id: employee?.id,
            employeeName: employee?.employeeName || '--',
            startDate: employee?.startDate
              ? new Date(employee.startDate).toLocaleDateString('en-GB')
              : '--',
            endDate: employee?.endDate
              ? new Date(employee.endDate).toLocaleDateString('en-GB')
              : '--',
              daysOccupied: employee?.daysOccupied ?? null,

          };
        });

        if (this.rowData?.length) {
          this.paginationPageSizeSelector =
            PaginationHelper.getPaginationPageSizeOptions(this.rowData);
        } else {
          this.paginationPageSizeSelector = [];
        }

        this.loading = false;
      },

      error: (err) => {
        console.log(err);
      },
    });
  }

  onClear() {
    this.startDate = null;
    this.endDate = null;
    this.dateRangeForm.reset();
    this.getEmployeesInProperties();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }
}
